/** Shared PO document total helpers (align with backend calculateTotals). */

export type PoTotalLineInput = {
  ordered_quantity?: number | null;
  unit_price?: number | null;
  line_total?: number | null;
  discount_percentage?: number | null;
  discount_amount?: number | null;
  vat_applicable?: boolean | null;
  vat_percentage?: number | null;
};

export type PoTotalHeaderInput = {
  discount_percentage?: number | null;
  freight_charges?: number | null;
  other_charges?: number | null;
  total_overhead?: number | null;
};

function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function lineTotalOf(line: PoTotalLineInput): number {
  if (line.line_total != null && line.line_total !== undefined) {
    return Number(line.line_total) || 0;
  }
  return (Number(line.ordered_quantity) || 0) * (Number(line.unit_price) || 0);
}

function lineDiscountOf(line: PoTotalLineInput, lineTotal: number): number {
  if (line.discount_amount != null && line.discount_amount !== undefined) {
    return Number(line.discount_amount) || 0;
  }
  return (lineTotal * (Number(line.discount_percentage) || 0)) / 100;
}

function lineVatOf(line: PoTotalLineInput, lineTotal: number, lineDiscount: number): number {
  if (line.vat_applicable === false) return 0;
  const net = lineTotal - lineDiscount;
  return (net * (Number(line.vat_percentage) ?? 5)) / 100;
}

/**
 * Document grand total in PO currency.
 * Matches backend PurchaseOrderService.calculateTotals:
 * line discounts + header discount on gross subtotal + sum(line VAT) + freight/other/overhead.
 * Line VAT is on the discounted line net. Header discount does not change that VAT base.
 * Do not apply a flat header 5% VAT — that inflates exempt / 0% lines.
 */
export function computePoDocumentGrandTotal(
  lines: PoTotalLineInput[],
  po: PoTotalHeaderInput,
  overheadTotal = 0
): number {
  const lineRows = lines.map((line) => {
    const lineTotal = round2(lineTotalOf(line));
    const discountAmount = round2(lineDiscountOf(line, lineTotal));
    const vatAmount = round2(lineVatOf(line, lineTotal, discountAmount));
    return { lineTotal, discountAmount, vatAmount };
  });
  const subtotal = round2(lineRows.reduce((sum, l) => sum + l.lineTotal, 0));
  const lineDiscount = round2(lineRows.reduce((sum, l) => sum + l.discountAmount, 0));
  const headerDiscount = round2((subtotal * (Number(po.discount_percentage) || 0)) / 100);
  const discountAmount = round2(lineDiscount + headerDiscount);
  const totalAfterDiscount = round2(subtotal - discountAmount);
  const vatAmount = round2(lineRows.reduce((sum, l) => sum + l.vatAmount, 0));
  const freight = round2(Number(po.freight_charges) || 0);
  const other = round2(Number(po.other_charges) || 0);
  const overhead = round2(overheadTotal || Number(po.total_overhead) || 0);
  return round2(totalAfterDiscount + vatAmount + freight + other + overhead);
}
